import { useFormikContext } from 'formik'

import type { ShipmentType } from '@b-stock/shipment-api-client'

import type { BidFormFormikValues } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import isEuTaxCurrency from '@/buyer-portal/helpers/euTaxCurrency'

import MaxVatExposureDisplay from './MaxVatExposureDisplay'
import useEuMaxVatExposure from './useEuMaxVatExposure'

// Bid-confirmation EU VAT max-exposure wiring (FR-008). Parallel to
// `MaxTaxExposureDisplayContainer` — same form-context read, same
// single-amount-form mapping (`formSingleBid === formProxyMaxBid`), just
// aimed at the EU controller.
//
// Renders `null` when the currency isn't in the EU allow-list (EUR/GBP) —
// the Canadian container handles CAD/USD. When both fire on the same
// listing (impossible under the current currency allow-lists, but a
// belt on the future) each self-gates.
const MaxVatExposureDisplayContainer = ({
  listingId,
  buyerAddressId,
  currencyCode,
  buyNowPrice,
  buyerFeeCents,
  shippingCents,
  // Held for parity with the Canadian container even though the EU
  // client doesn't currently key on shipmentType — a delivery-method
  // change still bubbles through the surrounding formik context and
  // will re-render this container.
  shipmentType: _shipmentType,
}: {
  listingId: string
  buyerAddressId: string | undefined
  currencyCode: string
  buyNowPrice?: number
  buyerFeeCents?: number
  shippingCents?: number
  shipmentType?: ShipmentType
}) => {
  const formikContext = useFormikContext<BidFormFormikValues>()
  const amountDollars = formikContext.values.amount || buyNowPrice || 0
  const formAmountCents = Math.round(amountDollars * 100)
  const isTaxable = isEuTaxCurrency(currencyCode)

  const data = useEuMaxVatExposure({
    isTaxable,
    listingId,
    formSingleBid: formAmountCents,
    buyerAddressId,
    formProxyMaxBid: formAmountCents,
    buyerFeeCents,
    shippingCents,
  })

  if (!isTaxable || !data) {
    return null
  }

  return <MaxVatExposureDisplay data={data} currencyCode={currencyCode} />
}

export default MaxVatExposureDisplayContainer
